/**
 * The shape of a thing lying on the ground.
 *
 * A block is a small cube of its faces. Anything drawn flat - an item, or
 * a plant - is its picture extruded, the way it is in the hand: "зараз всі
 * дропи виглядають як куб з текстурами". Both are built for the terrain
 * shader, which wants a tile in `layer`, a face `shade` and full `light`
 * on every vertex, and reads a geometry without them as black.
 *
 * Its own module rather than a private method so the model stage can draw
 * one and a check can look at it.
 */

import * as THREE from "three";
import { PICKUP_SIZE } from "../core/entity/pickup.js";
import { TILE_PX } from "../core/block/tiles.js";
import { paintTile, TILES, shafted } from "../core/block/tilePixels.js";
import { extrudeSprite, gripOf } from "../core/block/spriteMesh.js";
import { drawnFlat, itemTile } from "../core/block/items.js";

/** How thick a picture is, in blocks. The same as in the hand. */
export const PICKUP_THICKNESS = 2 / 16;
/** How tall a picture lies, against the cube's size: taller, being thin. */
export const PICKUP_PICTURE = PICKUP_SIZE * 1.8;

const cubes = new Map<number, THREE.BufferGeometry>();
const pictures = new Map<number, THREE.BufferGeometry>();

/** The geometry for this thing on the ground, shared by every copy of it. */
export function pickupGeometry(id: number): THREE.BufferGeometry {
  const tile = itemTile(id);
  return drawnFlat(id) ? pictureForTile(tile) : cubeForTile(tile);
}

function cubeForTile(tile: number): THREE.BufferGeometry {
  const cached = cubes.get(tile);
  if (cached !== undefined) return cached;

  const h = PICKUP_SIZE / 2;
  const faces: [number[], number[], number][] = [
    [[1, 0, 0], [h, -h, h, h, -h, -h, h, h, -h, h, h, h], 0.80],
    [[-1, 0, 0], [-h, -h, -h, -h, -h, h, -h, h, h, -h, h, -h], 0.80],
    [[0, 1, 0], [-h, h, h, h, h, h, h, h, -h, -h, h, -h], 1.00],
    [[0, -1, 0], [-h, -h, -h, h, -h, -h, h, -h, h, -h, -h, h], 0.55],
    [[0, 0, 1], [-h, -h, h, h, -h, h, h, h, h, -h, h, h], 0.90],
    [[0, 0, -1], [h, -h, -h, -h, -h, -h, -h, h, -h, h, h, -h], 0.70],
  ];

  const position: number[] = [];
  const normal: number[] = [];
  const uv: number[] = [];
  const layer: number[] = [];
  const shade: number[] = [];
  const light: number[] = [];
  const index: number[] = [];

  faces.forEach(([n, corners, lit], f) => {
    for (let i = 0; i < 4; i++) {
      position.push(corners[i * 3]!, corners[i * 3 + 1]!, corners[i * 3 + 2]!);
      normal.push(n[0]!, n[1]!, n[2]!);
      layer.push(tile);
      shade.push(lit);
      // Full daylight: the shader reads a missing light as the dark of a
      // cave, and everything dropped turned black the moment it left the hand.
      light.push(255);
    }
    // One whole tile across each face, in the block units the shader wants.
    uv.push(0, 0, 1, 0, 1, 1, 0, 1);
    const v = f * 4;
    index.push(v, v + 1, v + 2, v, v + 2, v + 3);
  });

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(position, 3));
  geometry.setAttribute("normal", new THREE.Float32BufferAttribute(normal, 3));
  geometry.setAttribute("uv", new THREE.Float32BufferAttribute(uv, 2));
  geometry.setAttribute("layer", new THREE.Float32BufferAttribute(layer, 1));
  geometry.setAttribute("shade", new THREE.Float32BufferAttribute(shade, 1));
  geometry.setAttribute("light", new THREE.Float32BufferAttribute(light, 1));
  geometry.setIndex(index);
  cubes.set(tile, geometry);
  return geometry;
}

/**
 * The picture, extruded and stood on its grip point, in the shader's terms.
 *
 * The extruder works in 0..1 within a tile, which is exactly the uv the
 * terrain shader wants once `layer` names the tile. Stood so the point
 * the hand would hold is at the origin, the same as in the hand, so a
 * dropped sabre lies the way it was carried.
 */
function pictureForTile(tile: number): THREE.BufferGeometry {
  const cached = pictures.get(tile);
  if (cached !== undefined) return cached;

  const spec = TILES[tile];
  const pixels = spec === undefined
    ? new Uint8Array(TILE_PX * TILE_PX * 4)
    : paintTile(tile, spec);
  const mesh = extrudeSprite(pixels, TILE_PX, PICKUP_THICKNESS);
  const grip = gripOf(pixels, TILE_PX, shafted(tile) ? "bottom" : "bottomLeft");

  const count = mesh.positions.length / 3;
  const position = new Float32Array(mesh.positions.length);
  for (let i = 0; i < mesh.positions.length; i += 3) {
    position[i] = (mesh.positions[i]! - grip.x) * PICKUP_PICTURE;
    position[i + 1] = (mesh.positions[i + 1]! - grip.y) * PICKUP_PICTURE;
    position[i + 2] = mesh.positions[i + 2]! * PICKUP_PICTURE;
  }
  const layer = new Float32Array(count).fill(tile);
  const shade = new Float32Array(count).fill(0.9);
  const light = new Float32Array(count).fill(255);

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(position, 3));
  geometry.setAttribute("uv", new THREE.Float32BufferAttribute(mesh.uvs, 2));
  geometry.setAttribute("layer", new THREE.BufferAttribute(layer, 1));
  geometry.setAttribute("shade", new THREE.BufferAttribute(shade, 1));
  geometry.setAttribute("light", new THREE.BufferAttribute(light, 1));
  geometry.setIndex(mesh.indices);
  geometry.computeVertexNormals();
  pictures.set(tile, geometry);
  return geometry;
}
